'use client';

import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import Link from 'next/link';
import { find, isEmpty, map, omit } from 'lodash';
import { useMediaQuery } from 'usehooks-ts';
import Button from '@/components/shared/Button';
import { FileUpload } from '@/components/shared/FileUpload';
import { Textarea } from '@/components/shared/Textarea';
import { Input, Select, Switch } from '@axa/design-system';
import type { Option } from '@axa/toolkit';
import { useContactForm } from './useContactForm';

type ContactFormProps = {
  categoryDepartments?: {
    id: string;
    title: string;
    departments?: {
      id: string;
      title: string;
      email: string;
    }[];
  }[];
  linkWithCompanyOptions?: {
    label: string;
    value: string;
  }[];
};

export const ContactForm = ({ categoryDepartments, linkWithCompanyOptions }: ContactFormProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const isTablet = useMediaQuery('(max-width: 1199px)');

  const [departmentOptions, setDepartmentOptions] = useState<Option[]>([]);

  const {
    form: {
      formState: { errors, isSubmitting },
      control,
      handleSubmit,
      register,
      resetField,
      watch
    },
    handleContactSubmit,
    registerWithMask
  } = useContactForm();

  const subjectCategoryOptions = useMemo(
    () =>
      map(categoryDepartments, (category) => ({
        label: category.title,
        value: category.id
      })),
    [categoryDepartments]
  );

  const handleChangeCategory = (categoryId?: string) => {
    const category = find(categoryDepartments, { id: categoryId });

    setDepartmentOptions(
      map(category?.departments, (department) => ({
        label: department.title,
        value: department.id
      })) || []
    );

    resetField('subject');
  };

  const subjectCategory = watch('subjectCategory');

  return (
    <form onSubmit={handleSubmit(handleContactSubmit)} className="flex flex-col gap-4">
      <div className="flex w-full flex-col gap-4 lg:flex-row lg:gap-6">
        <div className="flex flex-col gap-4 lg:w-1/2">
          <Controller
            control={control}
            name="subjectCategory"
            render={({ field }) => (
              <Select
                id="subjectCategory"
                label="Categoria de assunto (Obrigatório)"
                errorMessage={errors.subjectCategory?.message}
                hasError={!!errors.subjectCategory}
                options={subjectCategoryOptions}
                searchable={false}
                {...omit(field, 'value')}
                onChange={(value) => {
                  handleChangeCategory(value?.value.toString());
                  field.onChange(value?.value);
                }}
                value={find(subjectCategoryOptions, { value: field.value })}
              />
            )}
          />
          <Controller
            control={control}
            name="subject"
            render={({ field }) => (
              <Select
                id="subject"
                label="Assunto (Obrigatório)"
                errorMessage={errors.subject?.message}
                hasError={!!errors.subject}
                tooltipMessage={
                  isEmpty(subjectCategory) ? 'Selecione uma categoria de assunto' : undefined
                }
                disabled={isEmpty(subjectCategory)}
                options={departmentOptions}
                searchable={false}
                {...omit(field, 'value')}
                onChange={(value) => field.onChange(value?.value)}
                value={find(departmentOptions, { value: field.value })}
              />
            )}
          />
          <Input
            id="name"
            type="text"
            label="Nome (Obrigatório)"
            errorMessage={errors.name?.message}
            hasError={!!errors.name}
            {...register('name')}
          />
          <Input
            id="socialName"
            type="text"
            label="Nome Social"
            errorMessage={errors.socialName?.message}
            hasError={!!errors.socialName}
            tooltipMessage="Na AXA, respeitamos a diversidade de gênero e temos um compromisso com sua individualidade. Se preferir, registraremos seu nome social para uma comunicação mais autêntica. Nossos canais de atendimento estão à disposição para apoiar sempre que necessário."
            {...register('socialName')}
          />
          <Controller
            control={control}
            name="linkWithCompany"
            render={({ field }) => (
              <Select
                id="linkWithCompany"
                label="Vínculo com a Empresa (Obrigatório)"
                errorMessage={errors.linkWithCompany?.message}
                hasError={!!errors.linkWithCompany}
                options={linkWithCompanyOptions}
                searchable={false}
                {...omit(field, 'value')}
                onChange={(value) => field.onChange(value?.value)}
                value={find(linkWithCompanyOptions, { value: field.value })}
              />
            )}
          />
          <Input
            id="email"
            type="email"
            inputMode="email"
            label="E-Mail (Obrigatório)"
            errorMessage={errors.email?.message}
            hasError={!!errors.email}
            {...register('email')}
          />
          <Input
            id="phone"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            prefix="+55"
            label="Telefone"
            errorMessage={errors.phone?.message}
            hasError={!!errors.phone}
            {...registerWithMask('phone', ['(99) 9999-9999', '(99) 99999-9999'])}
          />
          <Input
            id="identity"
            type="text"
            inputMode="tel"
            autoComplete="off"
            label="CPF ou CNPJ (Obrigatório)"
            errorMessage={errors.identity?.message}
            hasError={!!errors.identity}
            {...registerWithMask('identity', ['999.999.999-99', '99.999.999/9999-99'])}
          />
        </div>
        <div className="flex flex-col gap-5 lg:w-1/2">
          <Controller
            control={control}
            name="message"
            render={({ field }) => (
              <Textarea
                id="message"
                label="Mensagem (Obrigatório)"
                rows={isMobile ? 5 : isTablet ? 10 : 12}
                errorMessage={errors.message?.message}
                hasError={!!errors.message}
                {...field}
              />
            )}
          />
          <div className="flex flex-row gap-2 lg:gap-4">
            <Controller
              control={control}
              name="authorization"
              render={({ field }) => (
                <Switch
                  id="authorization"
                  label="Autorizo a utilização dos meus dados. (Obrigatório)"
                  errorMessage={errors.authorization?.message}
                  hasError={!!errors.authorization}
                  className="flex-1"
                  {...field}
                  onChange={(event) => {
                    event.target.checked ? field.onChange('true') : field.onChange('false');
                  }}
                />
              )}
            />
            <Link
              href="/compromissos-asg/governanca/privacidade-de-dados"
              target="_blank"
              className="text-base font-regular text-blue-500 hover:underline"
            >
              Política de proteção de dados
            </Link>
          </div>
          <div className="flex items-center justify-between gap-2">
            <Controller
              control={control}
              name="file"
              render={({ field }) => (
                <FileUpload
                  id="file"
                  label="Faça upload de arquivos"
                  accept="image/*,.txt,.pdf,.doc,.docx,.xls,.xlsx,.csv,.zip,.rar,.7z"
                  errorMessage={errors.file?.message?.toString()}
                  hasError={!!errors.file}
                  {...omit(field, 'ref')}
                  onChange={(event) => {
                    if (event?.target?.files?.length === 0) return;

                    event?.target?.files
                      ? field.onChange(event.target.files)
                      : field.onChange(undefined);
                  }}
                />
              )}
            />
            <div className="flex-1">
              <Button type="submit" className="w-fit lg:hidden" disabled={isSubmitting}>
                Enviar mensagem
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-10 hidden lg:flex lg:justify-center">
        <Button type="submit" className="w-[180px]" disabled={isSubmitting}>
          Enviar mensagem
        </Button>
      </div>
    </form>
  );
};
